import { useMemo } from 'react';

import { AI_HEADER_IMAGE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker';

type ImageGenerationParams = {
	contentId: string;
	useRawPrompt: boolean;
	theme?: string;
};

type ImageGenerationErrorParams = ImageGenerationParams & {
	error: Error;
};

export type ImageGenerationCallbacks = {
	onGenerateStart: (params: ImageGenerationParams) => void;
	onGenerateSuccess: (params: ImageGenerationParams) => void;
	onGenerateError: (params: ImageGenerationErrorParams) => void;
	onGenerateAbort: (params: ImageGenerationParams) => void;
};

/**
 * Custom hook that provides callbacks for tracking AI header image generation experience.
 *
 * @returns {ImageGenerationCallbacks} Object containing callbacks for different stages of image generation
 */
export const useImageGenerationCallbacks = (): ImageGenerationCallbacks => {
	return useMemo(() => {
		const experienceTracker = getExperienceTracker();
		return {
			onGenerateStart: (params: ImageGenerationParams) => {
				experienceTracker.start({
					name: AI_HEADER_IMAGE_GENERATION,
					attributes: params,
				});
			},
			onGenerateSuccess: (params: ImageGenerationParams) => {
				experienceTracker.succeed({
					name: AI_HEADER_IMAGE_GENERATION,
					attributes: params,
				});
			},
			onGenerateError: (params: ImageGenerationErrorParams) => {
				experienceTracker.fail({
					name: AI_HEADER_IMAGE_GENERATION,
					error: params.error,
					attributes: {
						contentId: params.contentId,
						useRawPrompt: params.useRawPrompt,
						...(params.theme && { theme: params.theme }),
					},
				});
			},
			onGenerateAbort: (params: ImageGenerationParams) => {
				experienceTracker.abort({
					name: AI_HEADER_IMAGE_GENERATION,
					reason: 'Request aborted by user',
					attributes: params,
				});
			},
		};
	}, []);
};
